import React from 'react';
import { dgiDifferenceStatus, formatMoney, monthLabel, noCreditPurchases, PAYEE_LABELS, peoplePurchases, PURCHASE_REASONS, sumBy } from './taxModel';

const th = 'border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left text-[9px] font-semibold uppercase tracking-wide text-neutral-700';
const td = 'border border-neutral-300 px-1.5 py-1 text-[9.5px] text-neutral-900';
const tdNum = `${td} text-right tabular-nums`;

function NoCreditTable({ rows }) {
  const noName = rows.filter((row) => row.reason === 'sin_datos_empresa');
  return (
    <section className="mt-3">
      <p className="text-[10px] font-bold">Gastos de la empresa sin crédito fiscal de ITBMS</p>
      <p className="mt-1 border border-red-500 bg-red-50 px-2 py-1.5 text-[10px] leading-snug text-red-900">
        <strong>Estas compras fueron gastos de la empresa, pero NO se incluyen como crédito fiscal en el Formulario 430.</strong>{' '}
        {noName.length > 0 && `${noName.length} de ellas porque la factura no trae el nombre ni el RUC de la empresa. `}
        Su tratamiento en otros impuestos (por ejemplo, la renta) lo define la contadora.
      </p>
      <table className="mt-2 w-full border-collapse">
        <thead>
          <tr>{['Orden', 'Fecha', 'Proveedor', 'RUC proveedor', 'Base', 'ITBMS', 'Motivo'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.po_number} className={row.reason === 'sin_datos_empresa' ? 'bg-red-50' : ''}>
              <td className={td}>{row.po_number}</td><td className={td}>{row.date}</td><td className={td}>{row.supplier_name}</td><td className={td}>{row.supplier_ruc || '—'}</td>
              <td className={tdNum}>{formatMoney(row.subtotal)}</td><td className={tdNum}>{formatMoney(row.tax)}</td>
              <td className={`${td} font-semibold`}>{PURCHASE_REASONS[row.reason] || row.reason}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold"><td className={td} colSpan={4}>Total ({rows.length})</td><td className={tdNum}>{formatMoney(sumBy(rows, 'subtotal'))}</td><td className={tdNum}>{formatMoney(sumBy(rows, 'tax'))}</td><td className={td} /></tr>
        </tfoot>
      </table>
    </section>
  );
}

function PeopleTable({ rows }) {
  return (
    <section className="mt-4">
      <p className="text-[10px] font-bold">Pagos a ayudantes (servicios profesionales) y al dueño</p>
      <p className="mt-1 border border-neutral-400 bg-neutral-50 px-2 py-1.5 text-[10px] leading-snug text-neutral-800">
        Estos pagos <strong>no son compras</strong>: no generan crédito fiscal de ITBMS y no están en el Formulario 430. Se listan para que la contadora
        les dé el tratamiento que corresponda (renta, retenciones, dividendos).
      </p>
      <table className="mt-2 w-full border-collapse">
        <thead>
          <tr>{['Orden', 'Fecha', 'Beneficiario', 'Tipo', 'Total'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.po_number}>
              <td className={td}>{row.po_number}</td><td className={td}>{row.date}</td><td className={td}>{row.supplier_name}</td>
              <td className={td}>{PAYEE_LABELS[row.payee_type] || row.payee_type}</td>
              <td className={tdNum}>{formatMoney(row.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold"><td className={td} colSpan={4}>Total ({rows.length})</td><td className={tdNum}>{formatMoney(sumBy(rows, 'total'))}</td></tr>
        </tfoot>
      </table>
    </section>
  );
}

function DgiTable({ control }) {
  return (
    <section className="mt-4">
      <p className="text-[10px] font-bold">Control con los informes de ventas de la DGI</p>
      <table className="mt-2 w-full border-collapse">
        <thead>
          <tr>{['Mes', 'Ventas DGI (base)', 'Notas de crédito', 'ITBMS DGI', 'Comprobante', 'Ventas en el ERP', 'Diferencia'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {control.months.map((month) => (
            <tr key={month.month}>
              <td className={`${td} capitalize`}>{monthLabel(month.month)}</td>
              <td className={tdNum}>{month.entered ? formatMoney(month.sales_base) : '—'}</td>
              <td className={tdNum}>{month.entered ? formatMoney(month.credit_notes_base) : '—'}</td>
              <td className={tdNum}>{month.entered ? formatMoney(month.itbms) : '—'}</td>
              <td className={td}>{month.voucher || '—'}</td>
              <td className={tdNum}>{formatMoney(month.erp_base)}</td>
              <td className={td}>{dgiDifferenceStatus(month).text}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td className={td}>Total</td><td className={tdNum}>{formatMoney(control.dgi_sales_base)}</td><td className={tdNum}>{formatMoney(control.credit_notes_base)}</td>
            <td className={tdNum}>{formatMoney(control.dgi_itbms)}</td><td className={td} /><td className={tdNum}>{formatMoney(control.erp_sales_base)}</td><td className={td} />
          </tr>
        </tfoot>
      </table>
      <p className="mt-1 text-[9.5px] text-neutral-700">
        {control.active
          ? 'Las ventas del formulario (casilla 11) y las notas de crédito (402/403) salen de estos informes de la DGI.'
          : 'Las ventas del formulario salen de las facturas del ERP; este cuadro es solo de control.'}
      </p>
    </section>
  );
}

// Printed second sheet: what is not in the form but the accountant needs to see.
export function NoCreditAnnexSheet({ report }) {
  const noCredit = noCreditPurchases(report.purchases);
  const people = peoplePurchases(report.purchases);
  const control = report.dgi_control;
  const showControl = control?.months.some((month) => month.entered);
  if (!noCredit.length && !people.length && !showControl) return null;
  return (
    <div className="mx-auto w-full min-w-[720px] max-w-[210mm] bg-white p-4 font-sans text-neutral-950 shadow-lg print:max-w-none print:p-0 print:shadow-none" style={{ breakBefore: 'page' }} data-testid="no-credit-annex">
      <div className="border-b-2 border-neutral-800 pb-1">
        <p className="text-[11px] font-bold">Anexo — Detalle fuera del Formulario 430</p>
        <p className="text-[10px]">{report.taxpayer.legal_name} · RUC {report.taxpayer.ruc} DV {report.taxpayer.dv} · Período {report.period} ({report.start} al {report.end})</p>
      </div>
      {showControl && <DgiTable control={control} />}
      {noCredit.length > 0 && <NoCreditTable rows={noCredit} />}
      {people.length > 0 && <PeopleTable rows={people} />}
      <p className="mt-2 text-[8.5px] text-neutral-600">Generado por Valhalla ERP. Si una factura sí tiene los datos de la empresa, márcala en la pestaña Compras del ERP y se incluirá en el crédito.</p>
    </div>
  );
}
